import styled from '@emotion/styled';

import { useUpdateEffect } from '@innovamat/hooks';
import { getAcceptLanguage } from '@innovamat/localization';
import useResourceEvents from '../../hooks/use-resource-events';
import useSwipe from '../../hooks/use-swipe';
import usePlayerState from '../../providers/player-providers/use-player-state';
import PdfViewer from '../../resources/pdf-viewer';
import VideoPlayer from '../../resources/video-player';
import { getResource } from '../../state/selectors';

import { AppletResource } from '../../resources/applet-resource';

import useVideoContext from '../../providers/video-providers/use-video-context';
import GeniallyViewer from '../../resources/genially-viewer';
import GeogebraViewer from '../../resources/geogebra-viewer';
import { ManipulativeZone } from '../../resources/manipulative-zone';
import { WebAppletWrapper } from '../../resources/web-applet-wrapper';
import ResourceNotification from '../resource-notification';

const ResourceWrapper = styled.div<{ isInFullscreen: boolean }>`
  z-index: 0;
  background-color: ${(props) =>
    !props.isInFullscreen && props.theme.colors.dark['05']};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export default function ResourceContent() {
  const {
    currentResource,
    resource,
    currentStep,
    translations,
    fullscreen,
    fullscreenIOS,
    appletInfo,
    setEventData,
    axiosInstance,
    apolloServer,
    videoOptions,
    school,
    uid,
    autoplay,
  } = usePlayerState();

  const { progressList } = useVideoContext();

  const isInFullscreen = !!fullscreen?.active || fullscreenIOS;

  const { handleAppletIsCompleted } = useResourceEvents();
  const subResource = getResource(resource, currentResource);
  const handlers = useSwipe();

  const autoplaySub =
    resource && 'resources' in resource
      ? autoplay || subResource !== resource.resources[0]
      : autoplay;

  useUpdateEffect(() => {
    // TODO: THIS IS RERENDERING THE VIDEO PROVIDER.
    // handleResetVideoPlayer?.();
  }, [currentResource]);

  const thumbnail = (resource as any)?.thumbnail;

  const displayResource = () => {
    if (!subResource) return null;

    if (subResource.isBloqued) {
      return (
        <ResourceNotification
          text={translations['session.bloquedResource.message']}
          dataTestId="warningResourceNotEnabled"
        />
      );
    }

    if ('isManipulative' in subResource) {
      return (
        <ManipulativeZone
          fullscreen={fullscreen}
          key={subResource.id}
          language={subResource.language}
          thumbnail={thumbnail}
          title={subResource.title}
          setEventData={setEventData}
          apolloServer={apolloServer}
          axiosInstance={axiosInstance}
          appletInfo={appletInfo!}
          school={school}
          uid={uid}
        />
      );
    }

    if ('settings' in subResource) {
      return (
        <GeogebraViewer
          isInFullScreen={isInFullscreen}
          key={subResource.file}
          script={subResource.script!}
          file={subResource.file}
          settings={subResource.settings}
          translations={translations}
        />
      );
    }

    if ('pages' in subResource) {
      if (subResource.pages[currentStep].isBloqued) {
        return (
          <ResourceNotification
            text={translations['session.bloquedResource.message']}
            dataTestId="warningResourceNotEnabled"
          />
        );
      }
      return (
        <PdfViewer
          path={subResource.path}
          page={currentStep + 1}
          numPages={subResource.pages.length}
        />
      );
    }

    if ('src' in subResource) {
      return <GeniallyViewer src={subResource.src} />;
    }

    if ('url' in subResource) {
      return (
        <VideoPlayer
          videoId={subResource.id}
          key={subResource.url}
          fullscreen={fullscreen}
          url={subResource.url}
          defaultProgress={
            progressList[subResource.url] || subResource.progress
          }
          videoOptions={videoOptions}
          autoplay={autoplaySub}
          dataTestId="videoPlayerWrapper"
        />
      );
    }

    if ('sceneName' in subResource) {
      if (subResource.status === 'BUGS') {
        return (
          <ResourceNotification
            text={translations['session.buggedApplet.message']}
          />
        );
      }
      // CHECK BY TAG to know if is webcontent or not.
      if (subResource.tags.includes('web_content')) {
        const activityParameters = {
          type: 'web',
          layout: 'web',
          minDuration: 600,
          maxDuration: 1800,
          minStatements: 12,
          id: `${subResource.sceneName!}_${subResource.packOrVariation}`, //TODO: replace for subResource.uuid when possible
        };
        const { regionParameters, webContentsUrl, region, solverUrl } =
          appletInfo!;
        const language = region
          ? getAcceptLanguage(region, subResource.language!)
          : subResource.language;

        return (
          <WebAppletWrapper
            key={activityParameters.id}
            userParameters={{
              language,
              school,
              uid,
            }}
            regionalParameters={regionParameters}
            activityParameters={activityParameters!}
            onCloseApplet={() => {}}
            onAppletIsCompleted={handleAppletIsCompleted}
            webContentsUrl={webContentsUrl!}
            solverUrl={solverUrl!}
            setEventData={setEventData}
          />
        );
      }
      return (
        <AppletResource
          sceneName={subResource.sceneName!}
          packOrVariation={subResource.packOrVariation!}
          language={subResource.language!}
          onAppletIsCompleted={handleAppletIsCompleted}
          fullscreen={fullscreen}
          appletInfo={appletInfo!}
          thumbnail={thumbnail}
          title={subResource.title}
          setEventData={setEventData}
          apolloServer={apolloServer}
          axiosInstance={axiosInstance}
          school={school}
          uid={uid}
        />
      );
    }
    return null;
  };

  return (
    <ResourceWrapper
      {...handlers}
      isInFullscreen={fullscreen.active || fullscreenIOS}
    >
      {displayResource()}
    </ResourceWrapper>
  );
}
